import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { createClient } from '@supabase/supabase-js';
import { Button, Input, Box, Text, Flex } from '@pancakeswap/uikit';
import { useWeb3React } from '@web3-react/core/core/core';
import { useHistory } from 'react-router-dom';
import Page from 'views/PageBridge';
import { Container, StyledInput, StyledButton, Header, Title,NetworkIcon, StyledButton2 } from './BridgePageStyled';
import BridgeGuide from './BridgeGuide';

// Supabase client setup
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);
 
// Contract addresses
const bridgeContractAddress = '0x8A1d8f0D43f5A9e7fc4D84Dd01503d85e2Dc27A6';
const tokenContractAddress = '0xaDAaE082237cB1772c9e079dB95c117E6Dd0C5aF'; // Example: VIZSLASWAP on BSC

// Replace with your actual ABIs
const bridgeContractAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "_vizslaToken",
        "type": "address"
      },
      { 
        "internalType": "address",
        "name": "_initialLockedWallet",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_admin",
        "type": "address"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "constructor"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      },
      {
        "internalType": "string",
        "name": "ethAddress",
        "type": "string"
      }
    ],
    "name": "lockForBridge",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

const tokenContractAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "approve",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

const BridgePage: React.FC = () => {
  const { account } = useWeb3React();
  const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null);
  const [bridgeContract, setBridgeContract] = useState<ethers.Contract | null>(null);
  const [bscTokenContract, setBscTokenContract] = useState<ethers.Contract | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [ethAddress, setEthAddress] = useState<string>('');
  const [isApproved, setIsApproved] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState<Date | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [relayerBalance, setRelayerBalance] = useState<string>('0');

  useEffect(() => {
    const fetchRelayerBalance = async () => {
      const provider = new ethers.providers.JsonRpcProvider('https://mainnet.infura.io/v3/6802e594e8a94796a0a412dfe1feafc1');
      const tokenContract = new ethers.Contract(
        '0x8d435A46C143a08bC99af0967a550e580d8ff879',
        [
          {
            constant: true,
            inputs: [{ name: 'account', type: 'address' }],
            name: 'balanceOf',
            outputs: [{ name: '', type: 'uint256' }],
            type: 'function',
          },
        ],
        provider
      );
  
      try {
        const balance = await tokenContract.balanceOf('0xde6c77ac73661e5df07b6ed464d21c41d3d6d94b');
        const formatted = ethers.utils.formatUnits(balance, 18);
        setRelayerBalance(formatted);
      } catch (err) {
        console.error('Error fetching relayer balance:', err);
      }
    };
  
    fetchRelayerBalance();
  }, []);
  
  
  useEffect(() => {
    if (!lastSubmitted) return undefined; // biztosítjuk, hogy legyen visszatérési érték
  
    const timePassedInterval = setInterval(() => {
      const seconds = Math.floor((new Date().getTime() - lastSubmitted.getTime()) / 1000);
      setStatusMessage(`⏳ Processing..  You've been ${seconds} for your VIZSLASWAP tokens on the Ethereum network. (It may take up to 2 minutes.)`);
    }, 1000);
  
    const confirmationCheckInterval = setInterval(async () => {
      const { data, error } = await supabase
        .from('bridges')
        .select('*')
        .eq('user_address', account)
        .order('created_at', { ascending: false })
        .limit(1);
  
      if (data && data[0]?.confirmed) {
        setStatusMessage("✅ VIZSLASWAP tokens received on Ethereum!");
        clearInterval(timePassedInterval);
        clearInterval(confirmationCheckInterval);
      }
    }, 10000);
  
    return () => {
      clearInterval(timePassedInterval);
      clearInterval(confirmationCheckInterval);
    };
  }, [lastSubmitted, account]);
  
  
  
  useEffect(() => {
    const initializeProviderAndContracts = async () => {
      if (account) { 
        const providerInstance = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(providerInstance);

        const bridgeInstance = new ethers.Contract(bridgeContractAddress, bridgeContractAbi, providerInstance.getSigner());
        const bscTokenInstance = new ethers.Contract(tokenContractAddress, tokenContractAbi, providerInstance.getSigner());

        setBridgeContract(bridgeInstance);
        setBscTokenContract(bscTokenInstance);
      }
    };

    initializeProviderAndContracts();
  }, [account]);

  const approveTokens = async () => {
    if (!account || !provider || !bscTokenContract || !amount) return;

    const signer = provider.getSigner();
    const bscTokenContractWithSigner = bscTokenContract.connect(signer);  
    const amountInWei = ethers.utils.parseUnits(amount, 18); 

    try {
      const tx = await bscTokenContractWithSigner.approve(bridgeContractAddress, amountInWei);
      await tx.wait();
      setIsApproved(true);
      console.log("Tokens approved");
    } catch (error) {
      console.error("Approval failed", error);
    }
  };

  const lockForBridge = async () => {
    if (!account || !provider || !bridgeContract || !bscTokenContract || !ethAddress || !amount) return;

    const signer = provider.getSigner();
    const bridgeContractWithSigner = bridgeContract.connect(signer);

    const amountInWei = ethers.utils.parseUnits(amount, 18);

    try {
      // Lock BSC tokens
      const tx = await bridgeContractWithSigner.lockForBridge(amountInWei, ethAddress);
      await tx.wait();
      setLastSubmitted(new Date());
setStatusMessage("🔄 VIZSLASWAP Tokens locked on BSC, processing in progress on Ethereum...");

      // Insert transaction into Supabase
      const { data, error } = await supabase.from('bridges').insert([
        { user_address: account, eth_address: ethAddress, amount,  tx_hash: tx.hash.toLowerCase(), confirmed: false }
      ]);

      if (error) {
        console.error("Error inserting into Supabase", error);
      } else {
        console.log("Bridge transaction saved to Supabase", data);
      }
    } catch (error) {
      console.error("Error locking tokens for bridge", error);
    }
  };

  return (
    <Page>
    <Container>
    <Header>
  <NetworkIcon src="/images/wallet/000.png" alt="BNB" />
  <Title>BSC ➝ Ethereum Bridge</Title>
  <NetworkIcon src="/images/wallet/0274.png" alt="ETH" />
</Header>
  
      <StyledInput
        value={ethAddress}
        onChange={(e) => setEthAddress(e.target.value)}
        placeholder="Enter Ethereum address"
      />
  
  <StyledInput
  value={amount}
  onChange={(e) => {
    const value = e.target.value;
    const numericValue = Number(value);
if (!Number.isNaN(numericValue) && numericValue <= Number(relayerBalance)) {

      setAmount(value);
    }
  }}
  placeholder={`Amount (max: ${relayerBalance})`}
/>
<Text fontSize="14px" mt="10px" color="gold">For security, max limit refills multiple times a day.</Text>

      <StyledButton
        onClick={approveTokens}
        disabled={isApproved}
      >
        {isApproved ? 'Approved' : 'Approve Tokens'}
      </StyledButton>
  
      <StyledButton2
        onClick={lockForBridge}
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : 'Transfer to Ethereum'}
      </StyledButton2>
      <BridgeGuide />
 
    </Container>
    {statusMessage && (
  <Box mt="20px">
    <Text bold>{statusMessage}</Text>
  </Box>
)}
  </Page>
  );
};

export default BridgePage;
